import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Input } from "@/components/ui/primitives";
import { useAuditLogs } from "./hooks";

const PAGE_SIZE = 50;

function tone(action: string): "muted" | "success" | "warning" | "danger" {
  if (action.endsWith(".failed") || action.includes("deleted")) return "danger";
  if (action.endsWith(".success")) return "success";
  return "muted";
}

/** Today's date as YYYY-MM-DD in business time (Asia/Kolkata), matching how the
 * server interprets the date filters — robust regardless of the machine's tz. */
function todayIST(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const { data, isLoading, isError, error } = useAuditLogs({
    page,
    action: action.trim(),
    start: start || undefined,
    end: end || undefined,
  });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function setToday() {
    const t = todayIST();
    setStart(t);
    setEnd(t);
    setPage(1);
  }

  function clearFilters() {
    setAction("");
    setStart("");
    setEnd("");
    setPage(1);
  }

  const hasFilters = action.trim() !== "" || start !== "" || end !== "";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
          Audit log
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Security-relevant events: sign-ins, sign-outs and sensitive changes.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-64">
          <label htmlFor="audit_action" className="mb-1 block text-xs font-medium text-muted-foreground">
            Action
          </label>
          <Input
            id="audit_action"
            aria-label="Filter by action"
            placeholder="e.g. auth.login"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label htmlFor="audit_start" className="mb-1 block text-xs font-medium text-muted-foreground">
            From
          </label>
          <Input
            id="audit_start"
            type="date"
            aria-label="From date"
            value={start}
            max={end || undefined}
            onChange={(e) => {
              setStart(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label htmlFor="audit_end" className="mb-1 block text-xs font-medium text-muted-foreground">
            To
          </label>
          <Input
            id="audit_end"
            type="date"
            aria-label="To date"
            value={end}
            min={start || undefined}
            onChange={(e) => {
              setEnd(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Button variant="outline" onClick={setToday}>
          Today
        </Button>
        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters}>
            Clear
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Action</th>
              <th className="px-4 py-2.5 font-medium">Actor</th>
              <th className="px-4 py-2.5 font-medium">IP</th>
              <th className="px-4 py-2.5 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load the audit log."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No audit entries.
                </td>
              </tr>
            )}
            {data?.items.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                  {formatDateTime(e.created_at)}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={tone(e.action)}>{e.action}</Badge>
                </td>
                <td className="px-4 py-3">{e.actor_email ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.ip_address ?? "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {e.entity_type ? `${e.entity_type}${e.entity_id ? ` ${e.entity_id.slice(0, 8)}` : ""}` : ""}
                  {e.detail ? ` ${JSON.stringify(e.detail)}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{data ? `${data.total} event${data.total === 1 ? "" : "s"}` : ""}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
